// SpearBI Chakra theme, built from src/theme/tokens.js.
//
// Legacy aliases (`primary1`, `primary2`, `secondary1`, `secondary2`,
// `custom.*`, `darkPrimary*`) are kept because ~95 call sites read them off
// `useTheme()`. Their *values* are retuned to the new, legible palette so the
// whole app shifts without touching those call sites.

import { extendTheme } from "@chakra-ui/react";
import { neutral, brand, signal, type, radius, elevation, motion } from "./tokens";

const config = { initialColorMode: "light", useSystemColorMode: false };

const fonts = { heading: type.sans, body: type.sans, mono: type.mono };

const colors = {
  // --- New scales ---
  ink: neutral,
  brand,
  signal,

  // --- Legacy aliases, retuned ---
  // Was #58b6c0 (2.37:1 on white — failed AA wherever it carried text).
  primary1: brand[500], // #0E7C86 — 4.94:1
  primary2: brand[400], // #219AA6
  // Was #58c087 / #74ebd5; the latter was used as a hover background behind
  // white text, which was unreadable.
  secondary1: signal.up, // #12775F
  secondary2: brand[600], // #0A5F67
  textActive: neutral[0],
  textInactive: neutral[900],

  darkPrimary1: brand[300],
  darkPrimary2: brand[200],
  darkSecondary1: "#4FA98B",
  darkSecondary2: "#7FC9B2",

  gradients: {
    primary: `linear(to-r, ${brand[500]}, ${brand[400]})`,
    secondary: `linear(to-r, ${brand[400]}, ${brand[500]})`,
    darkPrimary: `linear(to-r, ${brand[700]}, ${brand[600]})`,
    darkSecondary: `linear(to-r, ${brand[600]}, ${brand[700]})`,
  },

  // Chakra's stock semantic scales, retuned at the stops the app actually
  // reads (charts pull `theme.colors.green["500"]` directly, and Alert/Badge
  // colorSchemes resolve through the same keys). Untouched stops fall through
  // to Chakra's defaults via extendTheme's deep merge.
  green: { 300: "#63A98C", 400: "#3F9E7E", 500: signal.up, 600: "#0E6350", 700: "#0B5343" },
  red: { 300: "#D97970", 400: "#CC5850", 500: signal.down, 600: "#A5352F", 700: "#8C2D28" },
  orange: { 300: "#D6A75B", 400: "#BE8420", 500: signal.warn, 600: "#8C560A", 700: "#734709" },
  yellow: { 400: "#D6A75B", 500: signal.warn, 600: "#8C560A" },
  blue: { 300: "#7FA8C4", 400: "#4A87B0", 500: signal.info, 600: "#245B80", 700: "#1D4A69" },
  teal: { 300: brand[300], 400: brand[400], 500: brand[500], 600: brand[600], 700: brand[700] },
  cyan: { 300: brand[200], 400: brand[300], 500: brand[400], 600: brand[500] },

  custom: {
    50: neutral[0],
    100: neutral[50],
    200: neutral[200],
    300: neutral[100],
    400: neutral[300],
    500: neutral[50],
    600: neutral[400],
    700: neutral[500],
    800: neutral[700],
    900: neutral[900],
  },
};

const textStyles = {
  smallText: { fontSize: type.size.micro },
  eyebrow: {
    fontSize: type.size.micro,
    fontWeight: type.weight.semibold,
    letterSpacing: type.tracking.label,
    textTransform: "uppercase",
    color: neutral[500],
  },
  display: {
    fontSize: type.size.display,
    fontWeight: type.weight.semibold,
    letterSpacing: type.tracking.display,
    lineHeight: 1.1,
    fontVariantNumeric: "tabular-nums",
    color: neutral[900],
  },
  h1: {
    fontSize: type.size.title,
    fontWeight: type.weight.semibold,
    letterSpacing: type.tracking.title,
    lineHeight: 1.25,
    color: neutral[900],
  },
  h2: {
    fontSize: type.size.lead,
    fontWeight: type.weight.semibold,
    letterSpacing: type.tracking.title,
    lineHeight: 1.3,
    color: neutral[800],
  },
  subtitle: {
    fontSize: type.size.small,
    fontWeight: type.weight.regular,
    lineHeight: 1.45,
    color: neutral[500],
  },
};

// ~157 call sites pass a legacy `colorScheme` to Buttons ("blue" for primary
// actions, "red" for destructive, "green" for confirm, and so on). Rather than
// swallow those — which would flatten every action to one colour — or let
// Chakra's stock saturated scales back in, map each intent onto the palette.
const INTENT = {
  blue: { base: brand[500], hover: brand[600], active: brand[700], soft: brand[50] },
  teal: { base: brand[500], hover: brand[600], active: brand[700], soft: brand[50] },
  cyan: { base: brand[400], hover: brand[500], active: brand[600], soft: brand[50] },
  brand: { base: brand[500], hover: brand[600], active: brand[700], soft: brand[50] },
  custom: { base: brand[500], hover: brand[600], active: brand[700], soft: brand[50] },
  green: { base: signal.up, hover: "#0E6350", active: "#0B5343", soft: signal.upSoft },
  red: { base: signal.down, hover: "#A5352F", active: "#8C2D28", soft: signal.downSoft },
  orange: { base: signal.warn, hover: "#8C560A", active: "#734709", soft: signal.warnSoft },
  yellow: { base: signal.warn, hover: "#8C560A", active: "#734709", soft: signal.warnSoft },
  purple: { base: signal.info, hover: "#245B80", active: "#1D4A69", soft: signal.infoSoft },
  gray: { base: neutral[600], hover: neutral[700], active: neutral[800], soft: neutral[100] },
};

const intentOf = (props) => INTENT[props.colorScheme] || INTENT.brand;

const components = {
  Button: {
    baseStyle: {
      fontWeight: type.weight.medium,
      borderRadius: radius.sm,
      letterSpacing: "0",
      transition: `background-color ${motion.fast}, border-color ${motion.fast}, color ${motion.fast}, box-shadow ${motion.fast}`,
      // The previous base style lifted every button 2px on hover, including
      // table-row buttons, which made dense grids jitter under the cursor.
      _focusVisible: { boxShadow: `0 0 0 2px ${neutral[0]}, 0 0 0 4px ${brand[500]}` },
      _focus: { boxShadow: "none" },
    },
    sizes: {
      xs: { fontSize: type.size.micro, h: "26px", px: 2.5 },
      sm: { fontSize: type.size.small, h: "32px", px: 3 },
      md: { fontSize: type.size.body, h: "38px", px: 4 },
    },
    variants: {
      primary: (props) => {
        const c = intentOf(props);
        return {
          bg: c.base,
          color: neutral[0],
          _hover: { bg: c.hover, _disabled: { bg: c.base } },
          _active: { bg: c.active },
        };
      },
      solid: (props) => {
        const c = intentOf(props);
        return {
          bg: c.base,
          color: neutral[0],
          _hover: { bg: c.hover, _disabled: { bg: c.base } },
          _active: { bg: c.active },
        };
      },
      secondary: {
        bg: signal.up,
        color: neutral[0],
        _hover: { bg: "#0E6350" },
        _active: { bg: "#0B5343" },
      },
      // Default action for most toolbars: reads as a control, not a call to
      // action. Dense screens have a dozen of these.
      subtle: {
        bg: neutral[0],
        color: neutral[700],
        border: "1px solid",
        borderColor: neutral[300],
        _hover: { bg: neutral[100], borderColor: neutral[400] },
        _active: { bg: neutral[200] },
      },
      outline: (props) => {
        const c = intentOf(props);
        return {
          bg: "transparent",
          border: "1px solid",
          borderColor: c.base,
          color: c.base,
          _hover: { bg: c.soft },
          _active: { bg: c.soft },
        };
      },
      ghost: (props) => {
        const c = props.colorScheme && props.colorScheme !== "brand" ? intentOf(props) : null;
        return {
          color: c ? c.base : neutral[600],
          _hover: { bg: c ? c.soft : neutral[100], color: c ? c.hover : neutral[900] },
          _active: { bg: c ? c.soft : neutral[200] },
        };
      },
      active: {
        bg: brand[50],
        color: brand[600],
        border: "1px solid",
        borderColor: brand[200],
      },
      danger: {
        bg: signal.down,
        color: neutral[0],
        _hover: { bg: "#A5352F" },
      },
    },
    defaultProps: { variant: "primary", size: "sm" },
  },

  Card: {
    baseStyle: {
      container: {
        bg: neutral[0],
        borderRadius: radius.md,
        border: "1px solid",
        borderColor: neutral[200],
        boxShadow: elevation.raised,
        overflow: "hidden",
        transition: `box-shadow ${motion.base}`,
      },
    },
  },

  Link: {
    baseStyle: {
      color: brand[500],
      transition: `color ${motion.fast}`,
      _hover: { textDecoration: "none", color: brand[600] },
      _focusVisible: { boxShadow: `0 0 0 2px ${brand[500]}`, borderRadius: radius.sm },
    },
  },

  Input: {
    sizes: {
      sm: { field: { fontSize: type.size.small, h: "32px", borderRadius: radius.sm } },
      md: { field: { fontSize: type.size.body, h: "38px", borderRadius: radius.sm } },
    },
    variants: {
      outline: {
        field: {
          bg: neutral[0],
          borderColor: neutral[300],
          color: neutral[800],
          _placeholder: { color: neutral[400] },
          _hover: { borderColor: neutral[400] },
          _focusVisible: {
            borderColor: brand[500],
            boxShadow: `0 0 0 1px ${brand[500]}`,
          },
        },
      },
      filled: {
        field: {
          bg: neutral[100],
          borderRadius: radius.sm,
          _hover: { bg: neutral[200] },
          _focusVisible: { bg: neutral[0], borderColor: brand[500] },
        },
      },
    },
    defaultProps: { variant: "outline", size: "sm" },
  },

  Select: {
    defaultProps: { variant: "outline", size: "sm" },
    variants: {
      outline: {
        field: {
          bg: neutral[0],
          borderColor: neutral[300],
          borderRadius: radius.sm,
          fontSize: type.size.small,
          _focusVisible: { borderColor: brand[500], boxShadow: `0 0 0 1px ${brand[500]}` },
        },
      },
    },
  },

  Badge: {
    baseStyle: {
      borderRadius: radius.sm,
      px: 1.5,
      py: "1px",
      fontSize: type.size.micro,
      fontWeight: type.weight.semibold,
      letterSpacing: type.tracking.label,
      textTransform: "uppercase",
    },
  },

  Table: {
    variants: {
      // Registered so `<Table variant="data">` picks up the shared dense recipe.
      data: {
        th: {
          bg: neutral[50],
          color: neutral[500],
          fontSize: type.size.micro,
          fontWeight: type.weight.semibold,
          letterSpacing: type.tracking.label,
          textTransform: "uppercase",
          borderColor: neutral[200],
          py: 2,
          px: 2.5,
        },
        td: {
          fontSize: type.size.data,
          color: neutral[700],
          borderColor: neutral[100],
          py: 1.5,
          px: 2.5,
          fontVariantNumeric: "tabular-nums",
        },
        tbody: { tr: { _hover: { bg: neutral[50] } } },
      },
    },
  },

  Menu: {
    baseStyle: {
      list: {
        borderRadius: radius.md,
        border: "1px solid",
        borderColor: neutral[200],
        boxShadow: elevation.overlay,
        py: 1,
        px: 1,
      },
      item: {
        borderRadius: radius.sm,
        fontSize: type.size.small,
        color: neutral[700],
        _hover: { bg: neutral[100] },
        _focus: { bg: neutral[100] },
      },
    },
  },

  Tooltip: {
    baseStyle: {
      borderRadius: radius.sm,
      bg: neutral[900],
      color: neutral[0],
      px: 2,
      py: 1,
      fontSize: type.size.caption,
      fontWeight: type.weight.medium,
      boxShadow: elevation.overlay,
    },
  },

  Tabs: {
    variants: {
      line: {
        tab: {
          fontSize: type.size.small,
          fontWeight: type.weight.medium,
          color: neutral[500],
          borderBottomWidth: "2px",
          _selected: { color: brand[600], borderColor: brand[500] },
          _hover: { color: neutral[800] },
        },
        tablist: { borderColor: neutral[200] },
      },
    },
    defaultProps: { variant: "line" },
  },

  Divider: { baseStyle: { borderColor: neutral[200], opacity: 1 } },

  Skeleton: {
    baseStyle: {
      borderRadius: radius.sm,
      // Chakra's default pink-ish skeleton clashed with the marine neutrals.
      _light: { "--skeleton-start-color": neutral[100], "--skeleton-end-color": neutral[200] },
    },
  },
};

const styles = {
  global: {
    body: {
      bg: neutral[50],
      color: neutral[700],
      fontSize: type.size.body,
      lineHeight: 1.5,
      WebkitFontSmoothing: "antialiased",
      MozOsxFontSmoothing: "grayscale",
    },

    // Was `*:focus` — that painted a 2px ring on every mouse click, including
    // on table cells and section headers. `:focus-visible` keeps the ring for
    // keyboard users only.
    "*:focus": { outline: "none" },
    "*:focus-visible": {
      outline: `2px solid ${brand[500]}`,
      outlineOffset: "2px",
      borderRadius: radius.sm,
    },

    // The previous theme put `transition: color, border-color, background-color
    // 0.3s` on `*`. On the ranging grid that is ~20,000 animated elements and
    // it made scrolling and hovering visibly stutter. Transitions now live on
    // the components that need them.

    "::selection": { bg: brand[100], color: brand[900] },

    "::-webkit-scrollbar": { width: "10px", height: "10px" },
    "::-webkit-scrollbar-track": { background: "transparent" },
    "::-webkit-scrollbar-thumb": {
      background: neutral[300],
      borderRadius: radius.pill,
      border: `2px solid ${neutral[50]}`,
    },
    "::-webkit-scrollbar-thumb:hover": { background: neutral[400] },
    "::-webkit-scrollbar-corner": { background: "transparent" },

    "@media (prefers-reduced-motion: reduce)": {
      "*, *::before, *::after": {
        animationDuration: "0.01ms !important",
        animationIterationCount: "1 !important",
        transitionDuration: "0.01ms !important",
        scrollBehavior: "auto !important",
      },
    },
  },
};

const spearTheme = extendTheme({
  config,
  colors,
  fonts,
  textStyles,
  components,
  styles,
  radii: { sm: radius.sm, md: radius.md, lg: radius.lg, xl: radius.lg, full: radius.pill },
  shadows: {
    sm: elevation.raised,
    md: elevation.raised,
    lg: elevation.overlay,
    xl: elevation.overlay,
    outline: `0 0 0 2px ${brand[200]}`,
  },
});

export default spearTheme;
